import { CSSProperties, useEffect, useRef, useState } from 'react';
import type { HmdInfo, HmdScreenshotResult } from '../services/pcAgent';
import type { HmdIpdStatus } from '../types/database';
import HmdCastView from './HmdCastView';

/** Shell version that can cast its display (CastStreamer in the headset agent). */
const HMD_CAST_MIN_SHELL = '1.0.21';
function shellAtLeast(v: string | undefined, min: string): boolean {
  if (!v) return false;
  const a = v.replace(/^v/, '').split('.').map((x) => parseInt(x, 10) || 0);
  const b = min.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < 3; i++) { if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0); }
  return true;
}

type SwitchFn = (fn: string, on?: boolean) => Promise<{ success: boolean; error?: string; data?: { status?: number } }>;

// Aan/Uit for one headset system switch (SFS_* enum name). Reads the state
// when mounted; after a write it re-reads 1.5 s later because the shell
// answers the very next read with the value from BEFORE the write.
// Verified 2026-09-07 on PUI 5.15.7 for SFS_GLOBAL_VST (passthrough) and
// SFS_NAVGATION_SWITCH (dock bar).
function SwitchRow({ label, fn, onHmdSwitch, rowStyle, keyStyle, btn, onTitle, offTitle, onText, offText }: {
  label: string; fn: string; onHmdSwitch: SwitchFn;
  rowStyle: CSSProperties; keyStyle: CSSProperties; btn: (bg: string, disabled: boolean) => CSSProperties;
  onTitle: string; offTitle: string; onText: string; offText: string;
}) {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string>('');
  // Read once when the row mounts. The callback is an inline arrow from the
  // tile (a new identity every render), so it must NOT be an effect
  // dependency: that re-read both switches on every tile render (agent log
  // full of "switch ok", 2026-09-07).
  const switchRef = useRef(onHmdSwitch);
  switchRef.current = onHmdSwitch;
  useEffect(() => {
    let alive = true;
    switchRef.current(fn).then(r => { if (alive && r.success && typeof r.data?.status === 'number') setOn(r.data.status === 1); });
    return () => { alive = false; };
  }, [fn]);
  const set = async (want: boolean) => {
    if (busy) return;
    setBusy(true); setNote('');
    const r = await onHmdSwitch(fn, want);
    if (!r.success) { setBusy(false); setNote(`Mislukt: ${r.error || 'geen antwoord'}`); return; }
    await new Promise(res => setTimeout(res, 1500));
    const back = await onHmdSwitch(fn);
    setBusy(false);
    if (back.success && typeof back.data?.status === 'number') {
      const now = back.data.status === 1;
      setOn(now);
      setNote(now === want ? '' : 'Headset meldt nog de oude stand');
    } else {
      setOn(want);
    }
  };
  return (
    <div style={rowStyle}>
      <span style={keyStyle}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" style={btn(on === true ? '#2B6AC0' : '#54626f', busy || on === true)} disabled={busy || on === true} onClick={() => set(true)} title={onTitle}>Aan</button>
        <button type="button" style={btn(on === false ? '#2B6AC0' : '#54626f', busy || on === false)} disabled={busy || on === false} onClick={() => set(false)} title={offTitle}>Uit</button>
        <span style={{ fontSize: 12, opacity: 0.8 }}>{note || (on === null ? '' : on ? onText : offText)}</span>
      </span>
    </div>
  );
}

// Read-only value of a headset system switch that is not on/off. Used for
// SFS_IMMERSIVE_MODE (0..100): the shell pins it to 0 at boot (shell >= 1.0.20,
// BootDefaults.java) and a live write breaks the home until a reboot, so the
// sheet only shows what the headset reports.
function ValueRow({ label, fn, onHmdSwitch, rowStyle, keyStyle, render, title }: {
  label: string; fn: string; onHmdSwitch: SwitchFn;
  rowStyle: CSSProperties; keyStyle: CSSProperties;
  render: (value: number) => string; title: string;
}) {
  const [value, setValue] = useState<number | null>(null);
  const switchRef = useRef(onHmdSwitch);
  switchRef.current = onHmdSwitch;
  useEffect(() => {
    let alive = true;
    switchRef.current(fn).then(r => { if (alive && r.success && typeof r.data?.status === 'number') setValue(r.data.status); });
    return () => { alive = false; };
  }, [fn]);
  return (
    <div style={rowStyle} title={title}>
      <span style={keyStyle}>{label}</span>
      <span style={{ fontSize: 12, opacity: 0.8 }}>{value === null ? '–' : render(value)}</span>
    </div>
  );
}

interface HeadsetSheetProps {
  pcName: string;
  /** The headset paired to this PC (over Business Streaming), if its agent is connected. */
  hmd: HmdInfo | null;
  /** Serial Business Streaming reports, even when the agent socket is absent. */
  pairedSerial: string | null;
  /** Business Streaming link state from PicoDeviceService. */
  streamingConnected: boolean;
  isAdmin: boolean;
  canRestartStreaming: boolean;
  canReboot: boolean;
  busy: boolean;
  onRestartStreaming: () => void;
  onReboot: () => void;
  onScreenshot: () => void;
  /** Last screenshot taken from this sheet, shown inline once it arrives. */
  screenshot?: HmdScreenshotResult | null;
  /** Live view: the agent host to open the cast socket on, and how to end the cast explicitly. */
  liveView?: { host: string; serial: string; stopCast: () => Promise<unknown> } | null;
  /** Where the shadow's height comes from: the headset's own floor value or the z_offset_cm knob (admin). */
  shadowFloor?: { source: 'headset' | 'knob'; m?: number } | null;
  /** Fence watcher state from the agent (hmd_boundary in /api/status). */
  boundary?: { state: 'unknown' | 'ok' | 'lost' | 'rescanning' | 'failed'; rescans: number; episodes: number; last_restored_by?: string; since?: string } | null;
  /** The agent's IPD lock state (hmd_ipd in /api/status); null on an agent without it. */
  ipdLock?: HmdIpdStatus | null;
  /** Kid mode on/off or a value for this run, through the agent's lock (so the
   *  value is HELD, instead of being undone by the next tick). Resolves with the new state. */
  onHmdIpd?: (body: { manual?: boolean; mm?: number }) => Promise<{ success: boolean; error?: string; data?: HmdIpdStatus }>;
  /** Headset shell proxy call (IPD, re-relocalise); resolves with the agent's reply. */
  onHmdProxy?: (fn: string, args?: Record<string, string | number>) => Promise<{ success: boolean; error?: string; data?: any }>;
  /** Read (on undefined) or set a headset system switch by ToBService enum name (admin, paired headset only). */
  onHmdSwitch?: (fn: string, on?: boolean) => Promise<{ success: boolean; error?: string; data?: { status?: number } }>;
  onClose: () => void;
}

/**
 * The headset's own sheet, opened by tapping the BRIL badge on the PC tile.
 *
 * Shows only what changes an operator's next action; the rest is role-gated
 * (admins additionally see configuration-fault signals and the reboot). Raw
 * telemetry never lands here — it goes to the database and Insights.
 * Styling clones the other tile modals (fixed backdrop + centered white card).
 */
export default function HeadsetSheet({
  pcName, hmd, pairedSerial, streamingConnected, isAdmin, canRestartStreaming, canReboot, busy,
  onRestartStreaming, onReboot, onScreenshot, screenshot, liveView, shadowFloor, boundary, ipdLock, onHmdIpd, onHmdProxy, onHmdSwitch, onClose,
}: HeadsetSheetProps) {
  // Live view of the display (shell >= 1.0.21). Closing the viewer closes its
  // socket, which is what makes the agent stop the headset's encoder.
  const [castOpen, setCastOpen] = useState(false);
  const castable = !!liveView && !!liveView.host && shellAtLeast(hmd?.agent_version, HMD_CAST_MIN_SHELL);
  // IPD: pbsSetIPD accepts 62..72 mm (the buttons on the headset reach 58; below 62 stays manual).
  // The headset cannot report its IPD, so the value the agent holds (or the last
  // one we sent) is all we can show. With the agent's lock present, "Zet" goes
  // through it so the value is held for the run instead of being undone by the
  // next 62 mm tick; kid mode switches the lock off for the run.
  const [ipd, setIpd] = useState<number>(ipdLock?.mm ?? 62);
  const [ipdBusy, setIpdBusy] = useState(false);
  const [ipdNote, setIpdNote] = useState<string>('');
  const [relocBusy, setRelocBusy] = useState(false);
  const [relocNote, setRelocNote] = useState<string>('');
  const lockMm = ipdLock?.mm;
  useEffect(() => { if (lockMm !== undefined) setIpd(lockMm); }, [lockMm]);
  const sendIpd = async (mm: number) => {
    if ((!onHmdIpd && !onHmdProxy) || ipdBusy) return;
    const v = Math.max(62, Math.min(72, mm));
    setIpd(v); setIpdBusy(true);
    if (onHmdIpd) {
      const r = await onHmdIpd({ mm: v });
      setIpdBusy(false);
      setIpdNote(r.success ? `IPD ${v} mm ingesteld${r.data?.manual ? '' : ' en vastgehouden'}` : `Mislukt: ${r.error || 'geen antwoord'}`);
      return;
    }
    const r = await onHmdProxy!('setipd', { mm: v });
    setIpdBusy(false);
    const code = r?.data?.result;
    setIpdNote(r.success && (code === 0 || code === '0') ? `IPD ${v} mm ingesteld` : `Mislukt: ${r.error || 'code ' + code}`);
  };
  const setIpdManual = async (manual: boolean) => {
    if (!onHmdIpd || ipdBusy) return;
    setIpdBusy(true); setIpdNote('');
    const r = await onHmdIpd({ manual });
    setIpdBusy(false);
    if (!r.data) setIpdNote(`Mislukt: ${r.error || 'geen antwoord'}`);
    else if (!r.success) setIpdNote(`Stand opgeslagen, maar zetten mislukte: ${r.error || '?'}`);
  };
  // One line on what the lock is doing, for the row under the stepper.
  const lockText = (() => {
    if (!ipdLock) return '';
    if (!ipdLock.enforce) return 'uit (config hmd_ipd.enforce)';
    if (ipdLock.manual) return `handmatig (kind), knoppen op de bril; terug naar ${ipdLock.mm} mm na de ervaring`;
    const ago = ipdLock.last_set_at ? Math.max(0, Math.round((Date.now() - new Date(ipdLock.last_set_at).getTime()) / 1000)) : null;
    const last = ipdLock.last_ok === false ? `, laatste keer mislukt: ${ipdLock.last_error || '?'}` : ago != null ? `, laatst ${ago < 5 ? 'nu' : ago + ' s geleden'}` : ', nog niet gezet';
    return `automatisch ${ipdLock.mm} mm elke ${ipdLock.interval_seconds} s${last}`;
  })();
  const relocate = async () => {
    if (!onHmdProxy || relocBusy) return;
    setRelocBusy(true); setRelocNote('');
    const r = await onHmdProxy('resettracking');
    setRelocBusy(false);
    const code = r?.data?.result;
    setRelocNote(r.success && (code === 0 || code === '0') ? 'Herpositioneren gestart, kijk even rond' : `Mislukt: ${r.error || 'code ' + code}`);
  };
  const shotSrc = screenshot?.url || (screenshot?.image_b64 ? `data:image/jpeg;base64,${screenshot.image_b64}` : null);
  const agentOnline = hmd !== null;
  const lastSeenAgo = hmd ? Math.max(0, Math.round((Date.now() - new Date(hmd.last_seen).getTime()) / 1000)) : null;

  const rowStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #eef1f4' };
  const keyStyle: CSSProperties = { color: '#54626f' };
  const btn = (bg: string, disabled: boolean): CSSProperties => ({
    padding: '11px 14px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
    background: disabled ? '#c9d1da' : bg, color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', width: '100%',
  });

  // One verdict line, not a status dump.
  let verdict: { text: string; color: string };
  if (!pairedSerial && !hmd) verdict = { text: 'Geen headset gekoppeld aan deze PC', color: '#8a97a6' };
  else if (!agentOnline) verdict = { text: 'Headset-agent offline (geen verbinding met de headset)', color: '#c0392b' };
  else if (!streamingConnected) verdict = { text: 'Streaming niet verbonden — herstart streaming', color: '#c0392b' };
  else verdict = { text: 'Headset online, streaming verbonden', color: '#1e8e3e' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(16,32,46,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => { if (!busy) onClose(); }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 24,
          // Two columns while casting (details left, picture right), so the
          // live view gets real estate instead of being squeezed under the rows.
          width: castOpen ? 'min(1120px, calc(100vw - 32px))' : 'min(560px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', color: '#16202e',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0 }}>Headset · {pcName}</h3>
        <div style={{ fontSize: 13, fontWeight: 600, color: verdict.color, marginTop: 6 }}>{verdict.text}</div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: castOpen ? '0 1 360px' : '1 1 100%', minWidth: 280 }}>
        <div style={{ marginTop: 14 }}>
          <div style={rowStyle}>
            <span style={keyStyle}>Batterij</span>
            <span>{hmd?.battery != null ? `${hmd.battery}%` : '–'}{hmd?.charging ? ' ↑ laadt' : hmd?.charging === false ? ' ↓' : ''}</span>
          </div>
          <div style={rowStyle}>
            <span style={keyStyle}>Op het hoofd</span>
            <span>{hmd?.worn == null ? '–' : hmd.worn ? 'ja' : 'nee'}</span>
          </div>
          {hmd?.boundary !== undefined && (
            <div style={rowStyle}>
              <span style={keyStyle}>Speelgrens</span>
              <span style={{ color: hmd.boundary ? undefined : '#c0392b', textAlign: 'right' }}>
                {hmd.boundary
                  ? (boundary?.episodes ? `ja (${boundary.episodes}x weggevallen${boundary.last_restored_by ? `, hersteld door ${boundary.last_restored_by === 'rescan' ? 'herscan' : 'sessie/herstart'}` : ''})` : 'ja')
                  : boundary?.state === 'rescanning' ? 'nee, herscan bezig…'
                  : boundary?.state === 'failed' ? `nee, herscan hielp niet: start de ervaring of herstart`
                  : 'nee'}
              </span>
            </div>
          )}
          {agentOnline && lastSeenAgo != null && (
            <div style={rowStyle}>
              <span style={keyStyle}>Laatst gezien</span>
              <span>{lastSeenAgo < 5 ? 'nu' : `${lastSeenAgo}s geleden`}</span>
            </div>
          )}
          {(onHmdProxy || onHmdIpd) && (
            <>
              <div style={rowStyle}>
                <span style={keyStyle}>IPD</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button type="button" style={btn('#54626f', ipdBusy || ipd <= 62)} disabled={ipdBusy || ipd <= 62} onClick={() => sendIpd(ipd - 1)} title="1 mm smaller">−</button>
                  <span style={{ minWidth: 52, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{ipd} mm</span>
                  <button type="button" style={btn('#54626f', ipdBusy || ipd >= 72)} disabled={ipdBusy || ipd >= 72} onClick={() => sendIpd(ipd + 1)} title="1 mm wider">+</button>
                  <button type="button" style={btn('#2B6AC0', ipdBusy)} disabled={ipdBusy} onClick={() => sendIpd(ipd)} title={onHmdIpd ? 'Send this value to the headset and hold it for this run' : 'Send this value to the headset'}>Zet</button>
                </span>
              </div>
              {ipdLock && onHmdIpd && (
                <div style={rowStyle}>
                  <span style={keyStyle}>IPD-stand</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, textAlign: 'right' }}>
                    <button type="button" style={btn(!ipdLock.manual ? '#2B6AC0' : '#54626f', ipdBusy || !ipdLock.manual)} disabled={ipdBusy || !ipdLock.manual} onClick={() => setIpdManual(false)} title="Hold the headset at the room's IPD again (sends it right away)">Auto</button>
                    <button type="button" style={btn(ipdLock.manual ? '#e0791b' : '#54626f', ipdBusy || ipdLock.manual)} disabled={ipdBusy || ipdLock.manual} onClick={() => setIpdManual(true)} title="Kid: stop holding the IPD for this run so the buttons on the headset can reach 58 mm">Kind</button>
                    <span style={{ fontSize: 12, opacity: 0.8, maxWidth: 180 }}>{lockText}</span>
                  </span>
                </div>
              )}
              {ipdNote && (
                <div style={rowStyle}><span style={keyStyle}></span><span style={{ fontSize: 12, opacity: 0.8 }}>{ipdNote}</span></div>
              )}
              {onHmdProxy && (
              <div style={rowStyle}>
                <span style={keyStyle}>Tracking</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button type="button" style={btn('#0e8a8a', relocBusy)} disabled={relocBusy} onClick={relocate} title="Re-match the room map, like the triple tap on the headset. Silent; keeps LBE on.">Herpositioneer</button>
                  {relocNote && <span style={{ fontSize: 12, opacity: 0.8 }}>{relocNote}</span>}
                </span>
              </div>
              )}
            </>
          )}
          {onHmdSwitch && (
            <>
              <SwitchRow label="Passthrough" fn="SFS_GLOBAL_VST" onHmdSwitch={onHmdSwitch} rowStyle={rowStyle} keyStyle={keyStyle} btn={btn}
                onTitle="Show the room's cameras in the headset (SFS_GLOBAL_VST on)" offTitle="Back to the virtual view (SFS_GLOBAL_VST off)"
                onText="camerabeeld" offText="virtueel" />
              <SwitchRow label="Dock" fn="SFS_NAVGATION_SWITCH" onHmdSwitch={onHmdSwitch} rowStyle={rowStyle} keyStyle={keyStyle} btn={btn}
                onTitle="Show the PICO dock bar (SFS_NAVGATION_SWITCH on)" offTitle="Hide the PICO dock bar (SFS_NAVGATION_SWITCH off)"
                onText="zichtbaar" offText="verborgen" />
              <ValueRow label="Immersive" fn="SFS_IMMERSIVE_MODE" onHmdSwitch={onHmdSwitch} rowStyle={rowStyle} keyStyle={keyStyle}
                title="SFS_IMMERSIVE_MODE, 0 tot 100. De shell zet dit bij het opstarten op 0 (thuisscherm toont de zaal). Alleen-lezen: live wijzigen laat het thuisscherm zonder menu's achter tot een herstart."
                render={v => v === 0 ? '0 (thuis toont de zaal)' : `${v} (virtueel thuis, shell zet 0 bij herstart)`} />
            </>
          )}
          {isAdmin && (
            <>
              <div style={rowStyle}>
                <span style={keyStyle}>Serienummer</span>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{hmd?.serial || pairedSerial || '–'}</span>
              </div>
              <div style={rowStyle}>
                <span style={keyStyle}>Agent / PUI</span>
                <span>{hmd ? `${hmd.agent_version || '?'} / ${hmd.pui || '?'}` : '–'}</span>
              </div>
              {shadowFloor && hmd && (
                <div style={rowStyle}>
                  <span style={keyStyle}>Vloer (schaduw)</span>
                  <span title="Vloerhoogte die de headset zelf meldt (negatief = vloer onder het kaart-nulpunt); zonder headset-waarde geldt de knop z_offset_cm">
                    {shadowFloor.source === 'headset'
                      ? `via headset (${shadowFloor.m !== undefined ? shadowFloor.m.toFixed(2) + ' m' : '…'})`
                      : 'knop z_offset_cm (oude shell)'}
                  </span>
                </div>
              )}
              {hmd?.map_md5 && (
                <div style={rowStyle}>
                  <span style={keyStyle}>Guardian map</span>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{hmd.map_md5.slice(0, 8)}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
          {canRestartStreaming && (
            <button style={btn('#0e8a8a', busy || !agentOnline)} disabled={busy || !agentOnline} onClick={onRestartStreaming}>
              Herstart streaming (headset)
            </button>
          )}
          {liveView && !castOpen && (
            <button
              style={btn('#5b3fd6', busy || !agentOnline || !castable)}
              disabled={busy || !agentOnline || !castable}
              title={agentOnline && !castable ? `Headset-shell ${hmd?.agent_version || '?'} kan nog niet meekijken (vanaf ${HMD_CAST_MIN_SHELL})` : 'Kijk live mee met wat de bezoeker ziet'}
              onClick={() => setCastOpen(true)}
            >
              Live meekijken
            </button>
          )}
          {isAdmin && (
            <button style={btn('#2B6AC0', busy || !agentOnline)} disabled={busy || !agentOnline} onClick={onScreenshot}>
              Screenshot
            </button>
          )}
          {canReboot && (
            <button style={btn('#e0791b', busy || !agentOnline)} disabled={busy || !agentOnline} onClick={onReboot}>
              Herstart headset
            </button>
          )}
        </div>
        </div>

        {castOpen && liveView && (
          // Capped at 64vh wide so the square picture can never be taller than
          // the screen; the canvas keeps its aspect from the column width.
          <div style={{ flex: '1 1 420px', minWidth: 300, maxWidth: 'min(100%, 64vh)' }}>
            <HmdCastView host={liveView.host} serial={liveView.serial} profile="normal" stopCast={liveView.stopCast} onClose={() => setCastOpen(false)} />
          </div>
        )}
        </div>
        {agentOnline && liveView && !castable && (
          <div style={{ fontSize: 12, color: '#8a97a6', marginTop: 8 }}>
            Live meekijken vraagt headset-shell {HMD_CAST_MIN_SHELL} of nieuwer (nu {hmd?.agent_version || 'onbekend'}); de agent werkt de shell bij via het hmd_shell-doel van de ruimte.
          </div>
        )}

        {screenshot && (
          // Capped: the card is much wider while casting and a full-width
          // still would dwarf the live picture next to it.
          <div style={{ marginTop: 14, maxWidth: 460 }}>
            {shotSrc ? (
              <a href={screenshot.url || shotSrc} target="_blank" rel="noreferrer" title="Open in nieuw venster">
                <img src={shotSrc} alt="Screenshot van de headset" style={{ width: '100%', borderRadius: 8, display: 'block' }} />
              </a>
            ) : (
              <div style={{ fontSize: 12, color: '#8a97a6' }}>
                Screenshot opgeslagen op de headset (galerij); deze shell-versie stuurt hem nog niet mee.
              </div>
            )}
            {shotSrc && (
              <div style={{ fontSize: 11, color: '#8a97a6', marginTop: 4 }}>
                {screenshot.width}×{screenshot.height}{screenshot.bytes ? ` · ${Math.round(screenshot.bytes / 1024)} KB` : ''}
                {screenshot.storage_path ? ' · bewaard in incident-opslag (8 dagen)' : screenshot.upload_error ? ' · niet opgeslagen: ' + screenshot.upload_error : ''}
              </div>
            )}
          </div>
        )}

        {!agentOnline && (
          <div style={{ fontSize: 12, color: '#8a97a6', marginTop: 12 }}>
            Zonder agent-verbinding kan de headset niet op afstand bestuurd worden. Controleer WiFi of gebruik de PICO console.
          </div>
        )}

        <div style={{ textAlign: 'right', marginTop: 14 }}>
          <button onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', color: '#54626f', fontSize: 14, cursor: 'pointer' }}>Sluiten</button>
        </div>
      </div>
    </div>
  );
}
